import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CloseButton, Group, Indicator, Text } from "@mantine/core";
import { Dropzone, DropzoneProps } from "@mantine/dropzone";
import { IconFile, IconUpload, IconX } from "@tabler/icons-react";
import csrfToken from "../../lib/csrf-token";
import { mtsUrl } from "../../lib/mts-url";
import { AttachmentModel } from "../../types/attachment.types";
import Attachment from "../Attachment";

function createAttachmentFormData(file: File, tempId: string): FormData | null {
  const token = csrfToken();
  if (!token || token.length == 0) return null;
  const fd = new FormData();
  fd.append('authenticity_token', token);
  fd.append('attachment[file_name_upload]', file, file.name);
  fd.append('attachment[temp_attachable_id]', tempId);
  return fd;
}

interface FormAttachmentsProps extends Partial<DropzoneProps> {
  tempId: string;
  onUpdate?: Function;
}

export default function FormAttachments(props: FormAttachmentsProps) {
  const { tempId, onUpdate, ...restProps } = props;
  const [uploadedFiles, setUploadedFiles] = useState<AttachmentModel[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const onDropHandler = useCallback((files: File[]) => {
    const url = mtsUrl('attachments/create');
    setIsUploading(true);

    const uploaders = files.map(f =>
      axios.post(url, createAttachmentFormData(f, tempId))
    );

    Promise.all(uploaders).then(results => {
      setIsUploading(false);
      setUploadedFiles(uf => uf.concat(results.map(r => r.data)));
    });
  }, [tempId]);

  const onRemoveHandler = useCallback((attachmentId: number) => {
    const url = mtsUrl(`attachments/${attachmentId}`);
    axios.delete(url, {
      data: { authenticity_token: csrfToken() }
    }).then(() => {
      setUploadedFiles(uf => uf.filter(f => f.id !== attachmentId));
    });
  }, []);

  useEffect(() => {
    onUpdate && onUpdate(uploadedFiles);
  }, [uploadedFiles, onUpdate]);

  return (
    <Dropzone
      onDrop={onDropHandler}
      maxSize={10 * 1024 ** 2}
      loading={isUploading}
      {...restProps}
    >
      {uploadedFiles.length > 0 ? (
        <Group>
          {uploadedFiles.map((f, idx) => (
            <Indicator
              key={`indicator-${f.id}`}
              color="#ffffff00"
              style={{pointerEvents: 'all'}}
              label={
                <CloseButton
                  size="sm"
                  c="gray.2"
                  bg="dark.6"
                  radius="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveHandler(f.id)
                  }}
                />
              }
            >
              <Attachment key={f.id} attachment={f} />
            </Indicator>
          ))}
        </Group>
      ) : (
        <Group justify="center">
          <Dropzone.Idle>
            <IconFile size={47} stroke={1.5} />
          </Dropzone.Idle>
          <Dropzone.Accept>
            <IconUpload size={47} stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={47} stroke={1.5} />
          </Dropzone.Reject>
          <div>
            <Text size="lg">
              Drag files here or click to select
            </Text>
            <Text size="sm" c="dimmed">
              You may attach multiple files. 10MB max per file.
            </Text>
          </div>
        </Group>
      )}
    </Dropzone>
  )
}
